import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Link,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useUserStore } from '../store/userStore';
import { useAuthStore } from '../store/authStore';
import { UserRole } from '../types/models';
import { useForm, Controller } from 'react-hook-form';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { getRoleNames } from '../utils/roleUtils';
import { isAdmin, isSystemAdminUser } from '../utils/permissions';

interface UserFormData {
  email: string;
  full_name: string;
  password: string;
  roles: UserRole[];
  is_active: boolean;
}

interface UserEditData {
  email: string;
  full_name: string;
  password?: string;
  roles: UserRole[];
  is_active: boolean;
}

export const Users: React.FC = () => {
  const { users, isLoading, error, fetchUsers, createUser, updateUser, deleteUser } = useUserStore();
  const { user: currentUser } = useAuthStore(); // Get the current user from auth store
  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState<number | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<UserFormData>();

  const navigate = useNavigate();

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateClick = () => {
    setSelectedUser(null);
    reset({
      email: '',
      full_name: '',
      password: '',
      roles: [UserRole.ISSUER],
      is_active: true,
    });
    setFormDialogOpen(true);
  };

  const handleEditClick = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(userId);
      reset({
        email: user.email,
        full_name: user.full_name,
        roles: user.roles,
        is_active: user.is_active,
      });
      setFormDialogOpen(true);
    }
  };

  const handleDeleteClick = (userId: number) => {
    // Clear any previous error message when opening the delete dialog for a new user
    setDeleteError(null);
    setUserToDelete(userId);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: UserFormData) => {
    try {
      if (selectedUser) {
        // For update, omit password if it's empty
        const updateData: UserEditData = {
          email: data.email,
          full_name: data.full_name,
          roles: data.roles,
          is_active: data.is_active,
        };
        if (data.password) {
          updateData.password = data.password;
        }
        await updateUser(selectedUser, updateData);
      } else {
        // For create, password is required
        await createUser({
          email: data.email,
          full_name: data.full_name,
          password: data.password,
          roles: data.roles,
          is_active: data.is_active,
        });
      }
      setFormDialogOpen(false);
    } catch (error) {
      console.error('Error saving user:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (userToDelete) {
      try {
        setDeleteError(null);
        await deleteUser(userToDelete);
        setDeleteDialogOpen(false);
      } catch (error: any) {
        console.error('Error deleting user:', error);
        
        // Extract the error message from the response
        // Try multiple ways to get the error detail
        let errorMessage = error.message || 
                          error.response?.data?.detail || 
                          (typeof error.response?.data === 'string' ? error.response.data : 'Failed to delete user');
        
        console.log('Original error message:', errorMessage);
        
        // Make the error message more user-friendly and informative
        if (errorMessage.includes('Cannot delete user:')) {
          // The backend already provides specific reasons, but let's enhance them
          if (errorMessage.includes('created projects')) {
            errorMessage = 'This user has created projects in the system. Please reassign or delete these projects before deleting this user.';
          } else if (errorMessage.includes('created issues')) {
            errorMessage = 'This user has created issues in the system. Please reassign or resolve these issues before deleting this user.';
          } else if (errorMessage.includes('assigned as PIC')) {
            errorMessage = 'This user is assigned as a PIC (Person In Charge) to one or more issues. Please reassign these issues before deleting this user.';
          } else if (errorMessage.includes('assigned as approver')) {
            errorMessage = 'This user is assigned as an approver to one or more issues. Please reassign these issues before deleting this user.';
          }
        }
        
        console.log('Enhanced error message:', errorMessage);
        setDeleteError(errorMessage);
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs 
        items={[
          { label: 'Events', path: '/projects' },
          { label: "Rule Setting" }
        ]} 
      />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5" component="h2">
              Users
            </Typography>
            {isAdmin(currentUser) && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                color="primary"
                onClick={handleCreateClick}
              >
                Add User
              </Button>
            )}
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: '#e0e0e0' }}>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users
                  .filter(user => !isSystemAdminUser(user))
                  .map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{getRoleNames(user.roles)}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {isAdmin(currentUser) && (
                        <>
                          <IconButton
                            color="primary"
                            onClick={() => handleEditClick(user.id)}
                          >
                            <EditIcon />
                          </IconButton>
                          {!isSystemAdminUser(user) && (
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(user.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography variant="body1">
            Are you sure you want to delete this user? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Form Dialog */}
      <Dialog open={formDialogOpen} onClose={() => setFormDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="full_name"
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Full Name"
                      fullWidth
                      error={!!errors.full_name}
                      helperText={errors.full_name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>
              {!selectedUser && (
                <Grid item xs={12}>
                  <Controller
                    name="password"
                    control={control}
                    rules={{ required: !selectedUser ? 'Password is required for new users' : false }}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Password"
                        type="password"
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password?.message}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Roles</InputLabel>
                  <Controller
                    name="roles"
                    control={control}
                    rules={{ required: 'At least one role is required' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Roles"
                        multiple
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as UserRole[]).map((value) => (
                              <Chip key={value} label={getRoleNames([value])} />
                            ))}
                          </Box>
                        )}
                      >
                        {Object.values(UserRole).map((role) => (
                          <MenuItem key={role} value={role}>
                            {getRoleNames([role])}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={value}
                          onChange={(e) => onChange(e.target.checked)}
                        />
                      }
                      label="Active"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedUser ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>


    </Box>
  );
};
